import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, FileTextIcon, ZapIcon, MapPinIcon, ShieldCheckIcon, LockIcon, CreditCardIcon } from 'lucide-react';
const benefits = ['Menos desgaste emocional', 'Más claridad mental', 'Mejores límites', 'Más tranquilidad', 'Más control emocional'];
export function HeroSection() {
  return <section className="relative w-full bg-hero overflow-hidden pt-10 md:pt-16 pb-12 px-4" data-id="element-160">
      <div className="max-w-4xl mx-auto text-center" data-id="element-161">
        {/* Kicker */}
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-rose font-bold tracking-[0.18em] text-xs md:text-sm mb-7 uppercase" data-id="element-162">
          ⭐ Aula Mater ⭐
        </motion.div>

        {/* Title */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="font-serif font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] text-ink mb-5 uppercase tracking-tight" data-id="element-163">
          Cómo Trabajar con Gente Complicada
        </motion.h1>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-ink-soft text-base md:text-lg mt-5 max-w-2xl mx-auto leading-relaxed" data-id="element-164">
          Aprendé a manejar personas difíciles sin perder tu calma ni tu energía
          mental.
        </motion.p>

        {/* Italic rose accent */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.35
      }} className="font-serif-italic text-rose text-lg md:text-2xl mt-5" data-id="element-165">
          Claridad · Límites · Energía{' '}
          <span className="text-ink/40 mx-1" data-id="element-166">—</span> El método práctico
          completo
        </motion.div>

        {/* Mockup */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="relative max-w-2xl mx-auto mt-10 md:mt-14" data-id="element-167">
          <div className="absolute inset-8 bg-rose/15 blur-3xl rounded-full" data-id="element-168" />
          <div className="absolute inset-8 bg-gold/10 blur-3xl rounded-full translate-x-10" data-id="element-169" />
          <img src="https://cdn.magicpatterns.com/uploads/4Ra7yxLi7PGg3t7XN3AYBr/ChatGPT_Image_20_may_2026,_04_42_27_p.m..png" alt="Cómo Trabajar con Gente Complicada — Ebook" className="relative z-10 w-full h-auto rounded-2xl" data-id="element-170" />
        </motion.div>

        {/* Trust pills */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.55
      }} className="flex flex-wrap items-center justify-center gap-2.5 mt-10" data-id="element-171">
          {[{
          icon: FileTextIcon,
          text: '100% PDF'
        }, {
          icon: ZapIcon,
          text: 'Acceso en 5 min'
        }, {
          icon: MapPinIcon,
          text: 'Hecho en Argentina'
        }].map((p, i) => <div key={i} className="inline-flex items-center gap-2 bg-white border border-line text-ink text-sm font-medium px-4 py-2 rounded-full shadow-sm" data-id="element-172">
              <p.icon className="w-4 h-4 text-rose" data-id="element-173" />
              {p.text}
            </div>)}
        </motion.div>

        {/* Benefits */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.7
      }} className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6 max-w-2xl mx-auto" data-id="element-174">
          {benefits.map((b, i) => <span key={i} className="inline-flex items-center gap-1.5 text-sm text-ink-soft" data-id="element-175">
              <CheckIcon className="w-4 h-4 text-rose" strokeWidth={3} data-id="element-176" />
              {b}
            </span>)}
        </motion.div>

        {/* CTA */}
        <motion.a href="#oferta" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.8
      }} whileHover={{
        y: -2
      }} className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-deep text-white font-bold text-base md:text-lg px-12 py-5 rounded-full shadow-xl shadow-rose/30 mt-8 transition-all w-full sm:w-auto max-w-md" data-id="element-177">
          <ArrowRightIcon className="w-5 h-5" data-id="element-178" />
          QUIERO ACCEDER AHORA
        </motion.a>

        {/* Trust row */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.9
      }} className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5 text-xs md:text-sm text-muted" data-id="element-179">
          <span className="flex items-center gap-1.5" data-id="element-180">
            <ShieldCheckIcon className="w-4 h-4 text-rose" data-id="element-181" /> Garantía 7 días
          </span>
          <span className="text-rose/30" data-id="element-182">|</span>
          <span className="flex items-center gap-1.5" data-id="element-183">
            <LockIcon className="w-4 h-4 text-rose" data-id="element-184" /> Pago seguro
          </span>
          <span className="text-rose/30" data-id="element-185">|</span>
          <span className="flex items-center gap-1.5" data-id="element-186">
            <CreditCardIcon className="w-4 h-4 text-rose" data-id="element-187" /> Mercado Pago
          </span>
        </motion.div>

        {/* Stats bar */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1,
        duration: 0.5
      }} className="mt-8 inline-flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 bg-white/70 backdrop-blur border border-line rounded-full px-6 py-3 text-sm font-medium text-ink" data-id="element-188">
          <span data-id="element-189">
            <strong className="text-rose" data-id="element-190">+2.000</strong> lectores
          </span>
          <span className="text-line" data-id="element-191">•</span>
          <span data-id="element-192">
            <strong className="text-rose" data-id="element-193">6</strong> módulos
          </span>
          <span className="text-line" data-id="element-194">•</span>
          <span data-id="element-195">
            <strong className="text-rose" data-id="element-196">5</strong> bonus
          </span>
          <span className="text-line" data-id="element-197">•</span>
          <span data-id="element-198">
            ⭐ <strong className="text-rose" data-id="element-199">4.8/5</strong>
          </span>
        </motion.div>
      </div>
    </section>;
}